/**
 * Which OIDC flow the portal is configured for.
 *
 * The IdP client id carries the answer: a client provisioned for Authorization Code +
 * PKCE keeps the portal's own id and gains a `-code` suffix — `po-<portalId>-code`,
 * against the plain `po-<portalId>` of the legacy implicit one. One wlabel build serves
 * every portal, so the flow cannot be a build-time decision — and it cannot be a
 * deploy-time one either, because `OIDC_PARAMETERS` is written centrally and still says
 * `response_type=id_token token` even for a code client. The client id is the only
 * signal that is reliably per-portal and already in place.
 */

/** Suffix the IdP gives clients provisioned for Authorization Code + PKCE. */
export const CODE_CLIENT_SUFFIX = '-code';

/**
 * True when this portal must use Authorization Code + PKCE.
 *
 * Anything unrecognised answers `false` and keeps the portal on the implicit flow:
 * plain `po-<portalId>` clients, an empty string (the jest baseline in
 * `DEFAULT_DEV_VARS`), and the unreplaced `__OIDC_CLIENT_ID__` placeholder a
 * misconfigured deploy leaves behind. Falling back to the flow that works today is the
 * safe direction to be wrong in.
 *
 * Never cache the result at module scope: in development `window.OIDC_CLIENT_ID` does
 * not exist until the bootstrap response is merged onto `window` (`src/main.tsx`), so
 * a module-level read would evaluate to `false` for the whole session.
 */
export function isCodeFlow(clientId: string | undefined = window.OIDC_CLIENT_ID): boolean {
  return typeof clientId === 'string' && clientId.endsWith(CODE_CLIENT_SUFFIX);
}
